import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Empty,
} from 'antd';
import {
  BankOutlined, SearchOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import {
  FullHeightRow,
  FiltersTags,
  InputFilterDropdown,
  LatLng,
  SelectFilterDropdown,
  StyledTable,
  TableLoadingComponent,
  TruckIcon,
  Highlighteable,
  VehicleType,
} from '../../components';
import LocationInfo from '../../components/Location/LocationInfo';
import AssetDetailsModal from './AssetDetailsModal';
import LocationsSummary from './LocationsSummary';
import {
  getLocationsFilters,
  getCurrentInterfaceConfiguration,
  getSelectedCompany,
  isCurrentInterfaceConfigurationLoading,
  TableName,
} from '../../store/selectors';
import {
  clearInterfaceFilters,
  fetchLastUsedInterfaceConfiguration,
  mergeUpdateInterfaceFilters,
} from '../../store/actions';
import LocationAssetsService from '../../services/LocationAssetsService';
import { error } from '../../services/Logging';
import { DEFAULT_DATE_TIME } from '../../utils/constants/timeFormats';
import { ElapsedSpan } from '../../components/Vehicle/Data/DateTime.styled';

const getLicensePlate = (row) => row?.asset?.licensePlate;

const getAssetType = (row) => {
  const assetType = row?.asset?.type;
  return (!assetType || assetType === 'N/A') ? null : assetType;
};

const getCoordinates = (row) => row?.asset?.lastTrace?.coordinates;

const toDateValue = (value) => {
  if (typeof value === 'string') {
    return value;
  }

  if (!Array.isArray(value) || value.length < 3) {
    return value;
  }

  const [
    year, month, day, hour = 0, minute = 0, second = 0,
  ] = value;
  return new Date(year, month - 1, day, hour, minute, second).getTime();
};

const getArrivalAtLocation = (row) => toDateValue(row?.arrivalTime);

const getLastTrackingTime = (row) => toDateValue(row?.asset?.lastTrace?.time);

const renderAssetType = (row, t) => {
  const assetType = getAssetType(row);
  return assetType ? <VehicleType type={assetType} /> : t('LOCATIONS.UNKNOWN_ASSET_TYPE');
};

const renderLastUpdateLite = (value, t) => {
  if (!value) {
    return t('COMMON.MISSING');
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        flexDirection: 'column',
      }}
    >
      <div>{moment(value).format(DEFAULT_DATE_TIME)}</div>
      <ElapsedSpan layout="vertical">
        {moment.duration(moment(value).diff(moment())).humanize(true)}
      </ElapsedSpan>
    </div>
  );
};

const normalizeAssets = (response) => {
  const data = response?.data?.assets || [];
  return Array.isArray(data) ? data : [];
};

const getTotalAssets = (response, assetsLength) => (
  response?.data?.totalElements
  || response?.data?.total
  || assetsLength
);

const clampPagination = (page, size) => {
  const safePage = Number.isFinite(page) ? page : 0;
  const safeSize = Number.isFinite(size) ? size : 200;
  return {
    page: Math.max(0, safePage),
    size: Math.min(400, Math.max(1, safeSize)),
  };
};

const FILTERS = {
  LICENSE_PLATE: { name: 'LICENSE_PLATE' },
  LOCATION: 'LOCATION',
};

const StyledEmpty = styled(Empty)`
  padding-top: 40px;

  .ant {
    &-empty {
      &-image {
        height: 50px;
      }
      &-description {
        font-size: 16px;
      }
    }
    &icon {
      font-size: 50px;
    }
  }
`;

const LicensePlateButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${({ theme: currentTheme }) => currentTheme.color.primary};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
`;

const Locations = ({ size }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedCompany = useSelector(getSelectedCompany);
  const filters = useSelector(getLocationsFilters);
  const interfaceConfigurationLoading = useSelector(isCurrentInterfaceConfigurationLoading);
  const interfaceConfiguration = useSelector(getCurrentInterfaceConfiguration(TableName.Locations));
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingExists, setCheckingExists] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [{ page, size: pageSize }, setPagination] = useState({
    page: 0,
    size: 200,
  });
  const [total, setTotal] = useState(0);
  const [hasLocations, setHasLocations] = useState(null);
  const [summary, setSummary] = useState({
    assetsAtLocationsCount: 0,
    locationsWithAssetsCount: 0,
    emptyLocationsCount: 0,
  });
  const interfaceLoadDispatchedRef = useRef(false);
  const lastAssetsRequestKeyRef = useRef(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const interfaceLoaded = interfaceConfiguration?.tableName === TableName.Locations;

  const closeLicensePlateModal = () => {
    setSelectedAsset(null);
  };

  useEffect(() => {
    interfaceLoadDispatchedRef.current = true;
    dispatch(fetchLastUsedInterfaceConfiguration(TableName.Locations));
  }, [dispatch]);

  useEffect(() => {
    if (interfaceConfigurationLoading || interfaceLoaded) {
      interfaceLoadDispatchedRef.current = false;
    }
  }, [interfaceConfigurationLoading, interfaceLoaded]);

  useEffect(() => {
    const handleResize = (ev) => setWindowHeight(ev.currentTarget.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!selectedCompany) {
      setAssets([]);
      setTotal(0);
      setHasLocations(null);
      setLocationOptions([]);
      setSummary({
        assetsAtLocationsCount: 0,
        locationsWithAssetsCount: 0,
        emptyLocationsCount: 0,
      });
      return;
    }

    setCheckingExists(true);
    LocationAssetsService.getPredefinedLocationExists(selectedCompany)
      .then((response) => {
        setHasLocations(Boolean(response?.data));
      })
      .catch((err) => {
        error(`Failed to check predefined location exists: ${err}`);
        setHasLocations(false);
      })
      .finally(() => setCheckingExists(false));
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      return;
    }

    LocationAssetsService.getPredefinedLocationAssetsSummary(selectedCompany)
      .then((response) => {
        setSummary({
          assetsAtLocationsCount: response?.data?.assetsAtLocationsCount || 0,
          locationsWithAssetsCount: response?.data?.locationsWithAssetsCount
            || response?.data?.locationsWithVehiclesCount
            || 0,
          emptyLocationsCount: response?.data?.emptyLocationsCount
            || response?.data?.locationsWithoutVehiclesCount
            || 0,
        });
      })
      .catch((err) => {
        error(`Failed to fetch predefined location assets summary: ${err}`);
        setSummary({
          assetsAtLocationsCount: 0,
          locationsWithAssetsCount: 0,
          emptyLocationsCount: 0,
        });
      });
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      return;
    }

    LocationAssetsService.getPredefinedLocations(selectedCompany)
      .then((response) => {
        const locations = response?.data || [];
        const options = locations.map((location) => ({
          value: location.id,
          label: <LocationInfo location={location} />,
          labelText: location.name
            ? `${location.locationReferenceIdentifier} - ${location.name}`
            : location.locationReferenceIdentifier,
          searchValue: [location.locationReferenceIdentifier, location.name],
        }));
        setLocationOptions(options);
      })
      .catch((err) => {
        error(`Failed to fetch predefined locations: ${err}`);
        setLocationOptions([]);
      });
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany || hasLocations === false) {
      setAssets([]);
      setTotal(0);
      return;
    }

    if (!interfaceLoaded && (interfaceLoadDispatchedRef.current || interfaceConfigurationLoading)) {
      return;
    }

    const pagination = clampPagination(page, pageSize);
    const registrationNumber = filters[FILTERS.LICENSE_PLATE.name]
      && filters[FILTERS.LICENSE_PLATE.name][0];
    const locationIds = filters[FILTERS.LOCATION];
    const filter = (registrationNumber || (locationIds && locationIds.length))
      ? {
        registrationNumber,
        locationIds,
      }
      : null;
    const requestKey = JSON.stringify({
      selectedCompany,
      pagination,
      filter,
    });
    if (lastAssetsRequestKeyRef.current === requestKey) {
      return;
    }
    lastAssetsRequestKeyRef.current = requestKey;

    setLoading(true);
    LocationAssetsService.getPredefinedLocationAssets(
      selectedCompany,
      pagination,
      filter,
    )
      .then((response) => {
        const nextAssets = normalizeAssets(response);
        setAssets(nextAssets);
        setTotal(getTotalAssets(response, nextAssets.length));
      })
      .catch((err) => {
        error(`Failed to fetch predefined location assets: ${err}`);
        setAssets([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [
    selectedCompany,
    hasLocations,
    page,
    pageSize,
    filters,
    interfaceConfigurationLoading,
    interfaceLoaded,
  ]);

  const updateFilters = useCallback((nextFilter) => {
    dispatch(mergeUpdateInterfaceFilters(TableName.Locations, nextFilter));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [dispatch]);

  const clearFilters = useCallback(() => {
    dispatch(clearInterfaceFilters(TableName.Locations));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [dispatch]);

  const removeFilter = (filterName) => {
    updateFilters({ [filterName]: null });
  };

  const locationLabelsById = useMemo(() => {
    const labelMap = {};
    locationOptions.forEach((option) => {
      labelMap[option.value] = option.labelText
        || (Array.isArray(option.searchValue) && option.searchValue[0])
        || option.value;
    });
    return labelMap;
  }, [locationOptions]);

  const displayFilters = useMemo(() => ({
    ...filters,
    [FILTERS.LOCATION]: filters[FILTERS.LOCATION]
      ? filters[FILTERS.LOCATION].map((id) => locationLabelsById[id] || id)
      : null,
  }), [filters, locationLabelsById]);

  const columns = useMemo(() => ([
    {
      title: t('PROPERTIES.LICENSE_PLATE'),
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      filteredValue: filters[FILTERS.LICENSE_PLATE.name],
      filterDropdown: InputFilterDropdown(FILTERS.LICENSE_PLATE, updateFilters, t),
      filterIcon: <SearchOutlined />,
      render: (_, row) => {
        const licensePlate = getLicensePlate(row);
        return licensePlate ? (
          <LicensePlateButton
            type="button"
            onClick={() => setSelectedAsset(row)}
          >
            <TruckIcon style={{ fontSize: '14px', color: '#4ADE80' }} />
            <Highlighteable
              content={licensePlate}
              search={licensePlate}
            />
          </LicensePlateButton>
        ) : t('COMMON.MISSING');
      },
    },
    {
      title: t('PROPERTIES.ASSET_TYPE'),
      dataIndex: 'assetType',
      key: 'assetType',
      render: (_, row) => renderAssetType(row, t),
    },
    {
      title: t('PROPERTIES.LOCATION'),
      dataIndex: 'location',
      key: 'location',
      width: 400,
      filteredValue: filters[FILTERS.LOCATION],
      filterDropdown: SelectFilterDropdown(
        FILTERS.LOCATION,
        locationOptions,
        updateFilters,
        t,
        false,
        400,
        true,
      ),
      filterIcon: <SearchOutlined />,
      render: (_, row) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <BankOutlined style={{ color: '#38BDF8' }} />
          <LocationInfo location={row.location} />
        </span>
      ),
    },
    {
      title: t('PROPERTIES.COORDINATES'),
      dataIndex: 'coordinates',
      key: 'coordinates',
      render: (_, row) => (
        <LatLng
          copy
          latitude={getCoordinates(row)?.latitude}
          longitude={getCoordinates(row)?.longitude}
        />
      ),
    },
    {
      title: t('PROPERTIES.ARRIVAL_AT_LOCATION'),
      dataIndex: 'arrivalAtLocation',
      key: 'arrivalAtLocation',
      render: (_, row) => (
        getArrivalAtLocation(row)
          ? renderLastUpdateLite(getArrivalAtLocation(row), t)
          : t('COMMON.MISSING')
      ),
    },
    {
      title: t('PROPERTIES.LAST_TRACKING_UPDATE'),
      dataIndex: 'lastTrackingTime',
      key: 'lastTrackingTime',
      render: (_, row) => (
        getLastTrackingTime(row)
          ? renderLastUpdateLite(getLastTrackingTime(row), t)
          : t('COMMON.MISSING')
      ),
    },
  ]), [t, filters, locationOptions, updateFilters]);

  const onPageChange = (nextPage, nextSize) => {
    const newPage = Number.isFinite(nextPage) ? nextPage - 1 : page;
    const newSize = Number.isFinite(nextSize) ? nextSize : pageSize;
    setPagination(clampPagination(newPage, newSize));
  };

  const tablePagination = {
    showSizeChanger: true,
    showTotal: (value) => <span>{t('COMMON.TOTAL', { total: value })}</span>,
    total,
    disabled: loading,
    current: page + 1,
    pageSize,
    pageSizeOptions: ['1', '10', '20', '50', '100', '200', '400'],
    onChange: onPageChange,
  };

  let tableContent = (
    <StyledTable
      rowKey={(row, index) => row.assetId || row.id || row.vehicleId || index}
      columns={columns}
      dataSource={assets}
      size={size}
      scroll={{ y: windowHeight - 290 }}
      bordered
      loading={TableLoadingComponent(loading)}
      pagination={tablePagination}
    />
  );

  if (checkingExists) {
    tableContent = (
      <StyledEmpty
        description={t('COMMON.LOADING')}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  } else if (hasLocations === false) {
    tableContent = (
      <StyledEmpty
        description={t('LOCATIONS.NO_DEFINED')}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <FullHeightRow gutter={[10, 10]} style={{ paddingTop: '10px', marginBottom: '56px' }}>
      <Col span={24}>
        <LocationsSummary summary={summary} />
        <div style={{ marginBottom: '6px' }}>
          <FiltersTags
            filters={displayFilters}
            onRemoveFilter={removeFilter}
            onClearFilters={clearFilters}
            fullHeight={false}
            size="small"
          />
        </div>
        {tableContent}
        <AssetDetailsModal
          open={!!selectedAsset}
          onCancel={closeLicensePlateModal}
          asset={selectedAsset}
        />
      </Col>
    </FullHeightRow>
  );
};

Locations.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

Locations.defaultProps = {
  size: 'small',
};

export default Locations;
